// App-wide toast notifications — replaces window.alert() / confirm() so we
// don't drop modal browser chrome on the user mid-flow. A single
// <ToastContainer/> mounted at the AppShell level subscribes and renders.
//
// Usage:
//     showToast("保存成功", "success");
//     showToast(err.message, "error");
//     const ok = await showConfirm("确定删除?");

import { useSyncExternalStore } from "react";

export type ToastTone = "info" | "success" | "error" | "warn";

export interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
  // For confirm-style toasts; resolved when the user picks an option.
  confirm?: {
    onYes: () => void;
    onNo: () => void;
    yesLabel: string;
    noLabel: string;
    yesTone: "primary" | "danger";
  };
}

let toasts: Toast[] = [];
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

function nextId(): string {
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const DEFAULT_TTL_MS = 5000;
const ERROR_TTL_MS = 8000;

export function showToast(
  message: string,
  tone: ToastTone = "info",
  ttlMs?: number,
): void {
  const id = nextId();
  toasts = [...toasts, { id, message, tone }];
  notify();
  const ttl = ttlMs ?? (tone === "error" ? ERROR_TTL_MS : DEFAULT_TTL_MS);
  window.setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  }, ttl);
}

export function dismissToast(id: string): void {
  toasts = toasts.filter((t) => t.id !== id);
  notify();
}

// Confirm toast — returns a Promise resolving to true (yes) or false (no /
// dismissed). Useful as a drop-in for window.confirm() in event handlers.
export function showConfirm(
  message: string,
  opts: {
    yesLabel?: string;
    noLabel?: string;
    tone?: "primary" | "danger";
  } = {},
): Promise<boolean> {
  return new Promise((resolve) => {
    const id = nextId();
    const cleanup = (): void => {
      toasts = toasts.filter((t) => t.id !== id);
      notify();
    };
    toasts = [
      ...toasts,
      {
        id,
        message,
        tone: opts.tone === "danger" ? "warn" : "info",
        confirm: {
          yesLabel: opts.yesLabel ?? "确认",
          noLabel: opts.noLabel ?? "取消",
          yesTone: opts.tone ?? "primary",
          onYes: () => {
            cleanup();
            resolve(true);
          },
          onNo: () => {
            cleanup();
            resolve(false);
          },
        },
      },
    ];
    notify();
  });
}

export function useToasts(): Toast[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => toasts,
    () => toasts,
  );
}
